import React from "react";
import { useAuth, dashboardPathForRole } from "../lib/AuthContext";
import { Disclaimer } from "../components/Disclaimer";
import { useLandingConfig } from "../lib/useLandingConfig";

import { HeroSection } from "./landing/HeroSection";
import { MetricsBand } from "./landing/MetricsBand";
import { AudiencesSection } from "./landing/AudiencesSection";
import { EntitiesStrip } from "./landing/EntitiesStrip";
import { ProblemSection } from "./landing/ProblemSection";
import { HowItWorksSection } from "./landing/HowItWorksSection";
import { TiersSection } from "./landing/TiersSection";
import { WhySection } from "./landing/WhySection";
import { TestimonialsSection } from "./landing/TestimonialsSection";
import { FaqSection } from "./landing/FaqSection";
import { CtaFinalSection } from "./landing/CtaFinalSection";
import { LeadCaptureSection } from "./landing/LeadCaptureSection";

/**
 * LandingPage — composición de las secciones del home público.
 *
 * Estructura refactorizada (Sesión 2):
 *  - Cada sección vive en `src/pages/landing/*.jsx`
 *  - Datos estáticos (tiers, FAQs, etc.) en `src/pages/landing/data.js`
 *  - Las 3 secciones dinámicas (MetricsBand, EntitiesStrip, TestimonialsSection)
 *    consumen `useLandingConfig()` y renderizan null si están vacías.
 */
export default function LandingPage() {
  const { user } = useAuth();
  const isAuthed = Boolean(user);
  const primaryCtaHref = isAuthed ? dashboardPathForRole(user.role) : "/register";

  // CTAs específicos por nivel:
  // - Si no está logueado, llevan a /register con intent.
  // - Si está logueado, llevan al dashboard (el flujo de caso nuevo lo cubre el dashboard).
  const tierCtaHref = (intent) =>
    isAuthed ? dashboardPathForRole(user.role) : `/register?intent=${intent}`;

  // Config dinámica (Fase D + Sesión 1 cache): testimonios, entidades, métricas.
  const { testimonials, entities, metrics } = useLandingConfig();

  return (
    <div data-testid="landing-page" className="bg-white">
      <HeroSection user={user} primaryCtaHref={primaryCtaHref} />
      <MetricsBand metrics={metrics} />
      <AudiencesSection />
      <EntitiesStrip entities={entities} />
      <ProblemSection />
      <HowItWorksSection />
      <TiersSection tierCtaHref={tierCtaHref} />
      <WhySection />
      <TestimonialsSection testimonials={testimonials} />
      <FaqSection />
      <CtaFinalSection primaryCtaHref={primaryCtaHref} />
      <LeadCaptureSection />

      {/* Disclaimer global */}
      <section className="border-t border-gray-200 bg-white">
        <div className="container-prose py-12">
          <Disclaimer dataTestId="landing-global-disclaimer-expanded" />
        </div>
      </section>
    </div>
  );
}
